import excel from "exceljs";
import { importPart, type ImportPartBody } from "@/api/importPartService";
import { createMatCode } from "../materialCodeUtils";
import {
  fetchMaterialCodesBySupplier,
  fetchPartSKPBySupplier,
  fetchMaterial2ndTierProcesses,
  fetchMaterialOtherProcesses,
  fetchChildPartProcesses,
  fetchOEMPartProcesses,
  fetchSparePartProcesses,
  fetchOEMPartOtherProcesses,
  fetchSparePartOtherProcesses,
  fetchSupplier2nd,
  fetchReturnScrap,
  fetchAllSpecs,
  fetchDimensions,
  fetchLegacyMaterialCodes,
} from "./cachedQueries";
import { getCellStr, getCellNum } from "./sheetHelpers";
import type {
  ImportedBreakdown,
  ImportedChildPart,
  ImportedPartProcessRow,
  ImportedPartOtherProcessRow,
  ImportWarning,
  ParsedImportResult,
} from "./types";

// ─── Header parsing ──────────────────────────────────────────────────────────

const EMPTY_SENTINELS = new Set(["none", "null", "undefined", "n/a", "-", "na"]);
const normalizeCell = (val: string): string =>
  EMPTY_SENTINELS.has(val.trim().toLowerCase()) ? "" : val.trim();

const parseHeader = (sheet: excel.Worksheet) => {
  let partNo = "",
    ac = "",
    rt = "";

  const partName = "";

  const normalizeLabel = (raw: string) =>
    raw.replace(/[\s/:.]/g, "").toLowerCase();

  sheet.eachRow((row, rowNum) => {
    if (rowNum > 10) return;
    row.eachCell((cell, colNum) => {
      const label = normalizeLabel(cell.value?.toString().trim() ?? "");
      if (label === "partno") partNo = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
      if (label === "ac") ac = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
      if (label === "rt") rt = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
    });
  });

  return { partNo, partName, ac, rt: rt.replace(/^0+(?=\d)/, "") };
};

// ─── Section boundary detection ──────────────────────────────────────────────

const detectSectionRows = (sheet: excel.Worksheet) => {
  let materialCostRow = -1,
    oemProcessRow = -1,
    spareProcessRow = -1,
    oemOtherProcessRow = -1,
    spareOtherProcessRow = -1;

  sheet.eachRow((row, rowNum) => {
    const firstCellVal = row.getCell(1).value?.toString().trim() ?? "";
    if (firstCellVal === "Material Cost" && materialCostRow === -1)
      materialCostRow = rowNum;
    if (firstCellVal === "Part Process (OEM)" && oemProcessRow === -1)
      oemProcessRow = rowNum;
    if (firstCellVal === "Part Process (Spare)" && spareProcessRow === -1)
      spareProcessRow = rowNum;
    if (
      firstCellVal === "Part Other Process (OEM)" &&
      oemOtherProcessRow === -1
    )
      oemOtherProcessRow = rowNum;
    if (
      firstCellVal === "Part Other Process (Spare)" &&
      spareOtherProcessRow === -1
    )
      spareOtherProcessRow = rowNum;
  });

  return {
    materialCostRow,
    oemProcessRow,
    spareProcessRow,
    oemOtherProcessRow,
    spareOtherProcessRow,
  };
};

// ─── Child parts parsing ─────────────────────────────────────────────────────

// Resolves a name to an id via the given map.
const resolveToId = (map: Map<string, number>, name: string | undefined): number | null | undefined => {
  if (!name || name.toLowerCase() === "none") return null;
  return map.get(name.toLowerCase());
};

const parseChildParts = async (
  sheet: excel.Worksheet,
  childDataStart: number,
  childDataEnd: number,
  supplierCode: string,
  header: { partNo: string; ac: string; rt: string },
  isMigration: boolean,
) => {
  const childPartsMap = new Map<string, ImportedChildPart>();
  const childPartsOrder: string[] = [];
  let lastChildPartNo = "";
  // Lazy — only fetched the first time an ambiguous attribute match is hit, and only
  // during migration runs. Normal (non-migration) imports never had legacy data to
  // begin with, so skip the round-trip and go straight to the "latest" fallback.
  let legacyMaterialCodeMap: Map<string, string> | null = null;
  const getLegacyMaterialCodeMap = async (): Promise<Map<string, string>> => {
    if (!isMigration) return new Map();
    if (!legacyMaterialCodeMap) {
      const rows = await fetchLegacyMaterialCodes(
        header.partNo,
        header.ac,
        header.rt,
        supplierCode,
      );
      legacyMaterialCodeMap = new Map(rows.map((r) => [r.child_part_no, r.material_code]));
    }
    return legacyMaterialCodeMap;
  };

  const allMaterialCodes = await fetchMaterialCodesBySupplier(supplierCode);

  // Detect if BE has added attribute fields to the matcode list endpoint.
  // Use attribute matching when available — immune to component name changes.
  // Fall back to matcode string matching otherwise.
  const useAttrMatch =
    allMaterialCodes != null &&
    allMaterialCodes.length > 0 &&
    allMaterialCodes[0].material_supplier_id !== undefined;

  // String-based lookup — primary path when useAttrMatch is false, and a
  // legacy-code fallback when attribute matching finds zero candidates
  // (e.g. a supplier/spec/return-scrap name was renamed or truncated in the
  // old system so its current FK id no longer lines up with the material's).
  const materialCodeStrMap = new Map((allMaterialCodes ?? []).map((mc) => [mc.material_code, mc]));

  // Attribute-based lookup maps (primary path — built only when BE fields are available)
  let matSupplierByCode = new Map<string, number>();
  let supplier2ndByName = new Map<string, number>();
  let returnScrapByName = new Map<string, number>();
  let specByName = new Map<string, number>();
  let specByTypeAndName = new Map<string, number>(); // composite: "type|spec" → id (handles duplicate spec names)
  let dimByName = new Map<string, number>();

  if (useAttrMatch) {
    const [suppliers2nd, returnScraps, allSpecs, dimensions] =
      await Promise.all([
        fetchSupplier2nd(),
        fetchReturnScrap(),
        fetchAllSpecs(),
        fetchDimensions(),
      ]);
    // Derive supplier name → id directly from allMaterialCodes (first segment of material_code)
    // avoids dependency on getMaterialSuppliers whose supplier_code/name may not match Excel values
    matSupplierByCode = new Map(
      (allMaterialCodes ?? [])
        .filter((mc): mc is typeof mc & { material_supplier_id: number } => mc.material_supplier_id !== undefined)
        .map((mc) => [mc.material_code.split("_")[0], mc.material_supplier_id]),
    );
    supplier2ndByName = new Map(
      (suppliers2nd ?? []).map((s) => [s.name.toLowerCase(), s.id]),
    );
    returnScrapByName = new Map(
      (returnScraps ?? []).map((s) => [s.name.toLowerCase(), s.id]),
    );
    specByName = new Map(
      (allSpecs ?? []).map((s) => [s.spec_name.toLowerCase(), s.id]),
    );
    specByTypeAndName = new Map(
      (allSpecs ?? []).map((s) => [`${s.material_type_name.toLowerCase()}|${s.spec_name.toLowerCase()}`, s.id]),
    );
    dimByName = new Map(
      (dimensions ?? []).map((d) => [d.dimension_name.toLowerCase(), d.id]),
    );
  }

  const materialIdMap = new Map<string, number>();
  const ambiguousMatches: { childPartNo: string; materialCode: string; candidateCount: number; resolvedBy: "legacy" | "latest" }[] = [];
  const legacyResolvedMatches: { childPartNo: string; materialCode: string }[] = [];

  for (let r = childDataStart; r <= childDataEnd; r++) {
    const rawNo = getCellStr(sheet, r, 1);
    const childPartNo = rawNo || lastChildPartNo;
    if (!childPartNo) continue;
    lastChildPartNo = childPartNo;

    if (!childPartsMap.has(childPartNo)) {
      childPartsOrder.push(childPartNo);
      childPartsMap.set(childPartNo, {
        child_part_no: childPartNo,
        child_part_name: getCellStr(sheet, r, 2),
        qty: getCellNum(sheet, r, 3),
        material_supplier: getCellStr(sheet, r, 4),
        material_type: getCellStr(sheet, r, 5),
        material_spec: getCellStr(sheet, r, 6),
        dimension: getCellStr(sheet, r, 7),
        second_tier_supplier: getCellStr(sheet, r, 8),
        return_scrap_supplier: getCellStr(sheet, r, 9),
        first_tier_supplier: getCellStr(sheet, r, 10),
        thickness: getCellNum(sheet, r, 11),
        width: getCellNum(sheet, r, 12),
        bar_diameter: getCellNum(sheet, r, 13),
        shaft: getCellNum(sheet, r, 14),
        pipe_round_od: getCellNum(sheet, r, 15),
        pipe_rectangle_x: getCellNum(sheet, r, 16),
        pipe_rectangle_y: getCellNum(sheet, r, 17),
        length_mm: getCellNum(sheet, r, 18),
        amount: getCellNum(sheet, r, 19),
        ratio: getCellNum(sheet, r, 20),
        consumption: getCellNum(sheet, r, 21),
        part_weight: getCellNum(sheet, r, 22),
        scrap_weight: getCellNum(sheet, r, 23),
        material_cost: getCellNum(sheet, r, 24),
        is_purchased: getCellStr(sheet, r, 25).toLowerCase() === "yes",
        return_scrap_price: getCellNum(sheet, r, 26),
        admin: getCellNum(sheet, r, 27),
        profit: getCellNum(sheet, r, 28),
        percent: getCellNum(sheet, r, 29),
        other_percent: getCellNum(sheet, r, 30),
        total_material_cost: getCellNum(sheet, r, 31),
        second_tier_process_comment: getCellStr(sheet, r, 35),
        second_tier_processes: [],
        other_process_comment: getCellStr(sheet, r, 39),
        other_processes: [],
        cp_processes: [],
      });
    }

    const part = childPartsMap.get(childPartNo)!;

    if (!materialIdMap.has(childPartNo)) {
      // Always generate matcode string — used for warning messages and string-based fallback
      const materialCode = createMatCode(
        part.material_supplier,
        part.first_tier_supplier,
        part.second_tier_supplier,
        part.return_scrap_supplier,
        part.material_spec,
        part.dimension,
        {
          diameter: part.bar_diameter,
          rectangular_x: part.pipe_rectangle_x,
          rectangular_y: part.pipe_rectangle_y,
          thickness: part.thickness,
          length_mm: part.length_mm,
          round_od: part.pipe_round_od,
          shaft_od: part.shaft,
          width: part.width,
        },
      );
      part.material_code = materialCode;

      let matchedMaterial: (typeof allMaterialCodes)[0] | undefined;

      if (useAttrMatch) {
        // Resolve component names from Excel → current FK ids
        const matSupplierId = matSupplierByCode.get(part.material_supplier) ?? 0;
        const snd2Id = resolveToId(supplier2ndByName, part.second_tier_supplier);
        const scrapId = resolveToId(returnScrapByName, part.return_scrap_supplier);
        const specCompositeKey = `${(part.material_type ?? "").toLowerCase()}|${(part.material_spec ?? "").toLowerCase()}`;
        const specId = specByTypeAndName.get(specCompositeKey) ?? specByName.get((part.material_spec ?? "").toLowerCase()) ?? 0;
        const dimId = dimByName.get((part.dimension ?? "").toLowerCase()) ?? 0;

        const attrMatches = (allMaterialCodes ?? []).filter(
          (mc) =>
            mc.material_supplier_id === matSupplierId &&
            mc.material_spec_id === specId &&
            mc.material_dimension_id === dimId &&
            (mc.second_tier_supplier_id ?? null) === snd2Id &&
            (mc.return_scrap_supplier_id ?? null) === scrapId &&
            (mc.thickness ?? 0) === (part.thickness ?? 0) &&
            (mc.width ?? 0) === (part.width ?? 0) &&
            (mc.length_mm ?? 0) === (part.length_mm ?? 0) &&
            (mc.diameter ?? 0) === (part.bar_diameter ?? 0) &&
            (mc.round_od ?? 0) === (part.pipe_round_od ?? 0) &&
            (mc.shaft_od ?? 0) === (part.shaft ?? 0) &&
            (mc.rectangle_x ?? 0) === (part.pipe_rectangle_x ?? 0) &&
            (mc.rectangle_y ?? 0) === (part.pipe_rectangle_y ?? 0),
        );

        if (attrMatches.length === 1) {
          matchedMaterial = attrMatches[0];
        } else if (attrMatches.length > 1) {
          // Ambiguous: multiple materials share the same attributes (created before/after
          // a supplier or spec rename during migration). Attribute IDs can't tell them apart
          // (that's the definition of "ambiguous" here), so:
          // 1) Ask the legacy system which material_code this exact child part was built with.
          // 2) If the part never existed in the legacy system (or the code no longer exists
          //    here), fall back to the most recently created candidate (highest material_id).
          const legacyMap = await getLegacyMaterialCodeMap();
          const legacyCode = legacyMap.get(part.child_part_no);
          const byLegacy = legacyCode
            ? attrMatches.find((mc) => mc.material_code === legacyCode)
            : undefined;
          matchedMaterial =
            byLegacy ??
            [...attrMatches].sort((a, b) => b.material_id - a.material_id)[0];
          ambiguousMatches.push({
            childPartNo: part.child_part_no,
            materialCode,
            candidateCount: attrMatches.length,
            resolvedBy: byLegacy ? "legacy" : "latest",
          });
        } else {
          // Zero matches: attribute resolution failed outright (a supplier/spec/
          // return-scrap name was renamed, or truncated in the legacy system so
          // it no longer matches the master data's current full name). Fall back
          // to an exact material_code string match — legacy and the migrated DB
          // store material_code as the same raw denormalized string, so this
          // still finds the right material even when attribute ids don't line up.
          const legacyMap = await getLegacyMaterialCodeMap();
          const legacyCode = legacyMap.get(part.child_part_no);
          matchedMaterial = materialCodeStrMap.get(legacyCode ?? materialCode);
          if (matchedMaterial) {
            legacyResolvedMatches.push({ childPartNo: part.child_part_no, materialCode });
          }
        }

      } else {
        matchedMaterial = materialCodeStrMap.get(materialCode);
      }

      materialIdMap.set(childPartNo, matchedMaterial?.material_id ?? 0);

      if (!part.is_purchased) {
        part.material_cost = (matchedMaterial?.material_price ?? 0) * part.consumption;
      }
    }

    const stp2Name = getCellStr(sheet, r, 32);
    if (stp2Name) {
      part.second_tier_processes.push({
        name: stp2Name,
        value: getCellNum(sheet, r, 33),
        total: getCellNum(sheet, r, 34),
      });
    }

    const otherName = getCellStr(sheet, r, 36);
    if (otherName) {
      part.other_processes.push({
        name: otherName,
        value: getCellNum(sheet, r, 37),
        total: getCellNum(sheet, r, 38),
      });
    }

    const cpName = getCellStr(sheet, r, 40);
    if (cpName) {
      part.cp_processes.push({
        name: cpName,
        detail: getCellStr(sheet, r, 41),
        qty_1: getCellNum(sheet, r, 42),
        cost_per_qty_1: getCellNum(sheet, r, 43),
        qty_2: getCellNum(sheet, r, 44),
        cost_per_qty_2: getCellNum(sheet, r, 45),
        total: getCellNum(sheet, r, 46),
        total_x_qty: getCellNum(sheet, r, 47),
        tooling_name: getCellStr(sheet, r, 48),
        tooling_cost: getCellStr(sheet, r, 49),
      });
    }
  }

  const child_parts = childPartsOrder.map((no) => childPartsMap.get(no)!);
  return { child_parts, allMaterialCodes, materialIdMap, ambiguousMatches, legacyResolvedMatches };
};

// ─── Part process parsing ────────────────────────────────────────────────────

const parsePartProcessSection = (
  sheet: excel.Worksheet,
  sectionRow: number,
  endRow: number,
): { processes: ImportedPartProcessRow[]; admin: number; profit: number } => {
  const processes: ImportedPartProcessRow[] = [];
  let admin = 0,
    profit = 0;
  const dataStart = sectionRow + 2;

  for (let r = dataStart; r <= endRow; r++) {
    if (!admin) admin = getCellNum(sheet, r, 7);
    if (!profit) profit = getCellNum(sheet, r, 8);
    const name = getCellStr(sheet, r, 2);
    if (!name) continue;
    processes.push({
      name,
      detail: getCellStr(sheet, r, 3),
      unit: getCellNum(sheet, r, 4),
      price_per_unit: getCellNum(sheet, r, 5),
      total: getCellNum(sheet, r, 6),
    });
  }

  return { processes, admin, profit };
};

const parsePartOtherProcessSection = (
  sheet: excel.Worksheet,
  sectionRow: number,
  endRow: number,
): {
  processes: ImportedPartOtherProcessRow[];
  admin: number;
  profit: number;
  price: number;
} => {
  const processes: ImportedPartOtherProcessRow[] = [];
  let admin = 0,
    profit = 0,
    price = 0;
  const dataStart = sectionRow + 2;

  for (let r = dataStart; r <= endRow; r++) {
    if (!admin) admin = getCellNum(sheet, r, 4);
    if (!profit) profit = getCellNum(sheet, r, 5);
    if (!price) price = getCellNum(sheet, r, 7);
    const name = getCellStr(sheet, r, 2);
    if (!name) continue;
    processes.push({ name, price: getCellNum(sheet, r, 3) });
  }

  return { processes, admin, profit, price };
};

// ─── Name→ID resolution ──────────────────────────────────────────────────────

const buildNameMap = (list: { id: number; name: string }[] | undefined) =>
  new Map((list ?? []).map((item) => [item.name.toLowerCase(), item.id]));

const findId = (map: Map<string, number>, name: string) =>
  map.get(name.toLowerCase()) ?? 0;

// ─── Build ImportPartBody ────────────────────────────────────────────────────

const buildImportBody = (
  result: ImportedBreakdown,
  resolvedPartId: number,
  allMaterialCodes: Awaited<ReturnType<typeof fetchMaterialCodesBySupplier>>,
  materialIdMap: Map<string, number>,
  nameMaps: {
    sndTierMap: Map<string, number>;
    otherMap: Map<string, number>;
    cpMap: Map<string, number>;
    oemPartMap: Map<string, number>;
    sparePartMap: Map<string, number>;
    oemOtherMap: Map<string, number>;
    spareOtherMap: Map<string, number>;
  },
  isMigration: boolean,
  isActive: boolean,
): ImportPartBody => {
  const {
    sndTierMap,
    otherMap,
    cpMap,
    oemPartMap,
    sparePartMap,
    oemOtherMap,
    spareOtherMap,
  } = nameMaps;

  return {
    part: {
      part_id: resolvedPartId,
      part_number: result.header.part_no,
      part_name: result.header.part_name,
      ac: result.header.ac,
      rt: result.header.rt,
    },
    process: {
      ms_second_tier_process_ids: [
        ...new Set(
          result.child_parts
            .flatMap((cp) =>
              cp.second_tier_processes.map((stp) =>
                findId(sndTierMap, stp.name),
              ),
            )
            .filter(Boolean),
        ),
      ],
      ms_other_process_ids: [
        ...new Set(
          result.child_parts
            .flatMap((cp) =>
              cp.other_processes.map((op) => findId(otherMap, op.name)),
            )
            .filter(Boolean),
        ),
      ],
      ms_cp_process_ids: [
        ...new Set(
          result.child_parts
            .flatMap((cp) =>
              cp.cp_processes.map((cpp) => findId(cpMap, cpp.name)),
            )
            .filter(Boolean),
        ),
      ],
      ms_oem_process_ids: [
        ...new Set(
          result.part_process.oem_part_process
            .map((p) => findId(oemPartMap, p.name))
            .filter(Boolean),
        ),
      ],
      ms_spare_process_ids: [
        ...new Set(
          result.part_process.spare_part_process
            .map((p) => findId(sparePartMap, p.name))
            .filter(Boolean),
        ),
      ],
      ms_oem_other_process_ids: [
        ...new Set(
          result.part_other_process.oem_other_process
            .map((p) => findId(oemOtherMap, p.name))
            .filter(Boolean),
        ),
      ],
      ms_spare_other_process_ids: [
        ...new Set(
          result.part_other_process.spare_other_process
            .map((p) => findId(spareOtherMap, p.name))
            .filter(Boolean),
        ),
      ],
    },
    child_part: result.child_parts.map((cp) => {
      const materialId = materialIdMap.get(cp.child_part_no) ?? 0;
      const mc = allMaterialCodes?.find((m) => m.material_id === materialId);
      
      return {
        no: cp.child_part_no,
        name: cp.child_part_name,
        qty: cp.qty,
        material_id: materialId,
        amount: cp.amount,
        ratio: cp.ratio,
        volume: mc?.volume ?? 0,
        consumption: cp.consumption,
        part_weight: cp.part_weight,
        material_price: mc?.material_price ?? 0,
        material_cost: cp.is_purchased
          ? cp.material_cost
          : (mc?.material_price ?? 0) * cp.consumption,
        scrap_price: cp.scrap_weight,
        return_scrap_price: mc?.return_scrap_price ?? cp.return_scrap_price,
        is_purchase_part: cp.is_purchased,
        admin: cp.admin,
        profit: cp.profit,
        percent: cp.percent,
        second_tier_process_comment: cp.second_tier_process_comment,
        child_part_material_second_tier_processes_v2:
          cp.second_tier_processes.map((stp) => ({
            ms_second_tier_process_id: findId(sndTierMap, stp.name),
            cost: stp.value,
          })),
        other_process_comment: cp.other_process_comment,
        child_part_material_other_processes_v2: cp.other_processes.map(
          (op) => ({
            ms_other_process_id: findId(otherMap, op.name),
            cost: op.value,
          }),
        ),
        child_part_processes_v2: cp.cp_processes.map((cpp) => ({
          ms_cp_process_id: findId(cpMap, cpp.name),
          detail: cpp.detail,
          qty_1: cpp.qty_1,
          cost_per_qty_1: cpp.cost_per_qty_1,
          qty_2: cpp.qty_2,
          cost_per_qty_2: cpp.cost_per_qty_2,
          tooling_name: cpp.tooling_name,
          tooling_cost: parseFloat(cpp.tooling_cost) || 0,
        })),
      };
    }),
    part_process: {
      oem_part_process: result.part_process.oem_part_process.map((p) => ({
        ms_process_id: findId(oemPartMap, p.name),
        unit: p.unit,
        price_per_unit: p.price_per_unit,
        process_name: p.name,
        detail: p.detail,
        total_price: p.total,
      })),
      admin_oem_part_process: result.part_process.admin_oem,
      profit_oem_part_process: result.part_process.profit_oem,
      spare_part_process: result.part_process.spare_part_process.map((p) => ({
        ms_process_id: findId(sparePartMap, p.name),
        unit: p.unit,
        price_per_unit: p.price_per_unit,
        process_name: p.name,
        detail: p.detail,
        total_price: p.total,
      })),
      admin_spare_part_process: result.part_process.admin_spare,
      profit_spare_part_process: result.part_process.profit_spare,
    },
    part_other_process: {
      oem_other_process: result.part_other_process.oem_other_process.map(
        (p) => ({
          ms_process_id: findId(oemOtherMap, p.name),
          price: p.price,
          detail: "",
          process_name: p.name,
        }),
      ),
      admin_oem_part_other_process: result.part_other_process.admin_oem,
      oem_profit_part_other_process: result.part_other_process.profit_oem,
      spare_other_process: result.part_other_process.spare_other_process.map(
        (p) => ({
          ms_process_id: findId(spareOtherMap, p.name),
          price: p.price,
          detail: "",
          process_name: p.name,
        }),
      ),
      admin_spare_part_other_process: result.part_other_process.admin_spare,
      spare_profit_part_other_process: result.part_other_process.profit_spare,
    },
    is_migration: isMigration,
    is_active: isActive,
  };
};

// ─── Parse function (preview before submit) ─────────────────────────────────

export const parsePartFromExcel = async (
  file: File,
  supplierCode: string,
  isMigration = false,
  isActive = true,
): Promise<ParsedImportResult> => {
  const warnings: ImportWarning[] = [];

  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.getWorksheet("CBD");
  if (!sheet) throw new Error("Sheet 'CBD' not found");

  const header = parseHeader(sheet);

  // ── Resolve partId from API ────────────────────────────────────────────
  const partSKPList = await fetchPartSKPBySupplier(supplierCode);
  const matchedPart = partSKPList.find(
    (p) =>
      p.part_number === header.partNo &&
      p.ac === header.ac &&
      p.rt === header.rt,
  );
  if (!matchedPart && header.ac) {
    warnings.push({
      type: "part-not-found",
      message: `ไม่พบ Part: part_no="${header.partNo}" ac="${header.ac}" rt="${header.rt}"`,
    });
  }
  header.partName = matchedPart?.part_name ?? header.partName;
  const resolvedPartId = matchedPart?.part_id ?? 0;

  // ── Section boundaries ─────────────────────────────────────────────────
  const sections = detectSectionRows(sheet);

  // ── Child Parts ────────────────────────────────────────────────────────
  const childDataStart = sections.materialCostRow + 4;
  const childDataEnd =
    sections.oemProcessRow !== -1 ? sections.oemProcessRow - 1 : sheet.rowCount;

  const { child_parts, allMaterialCodes, materialIdMap, ambiguousMatches, legacyResolvedMatches } =
    await parseChildParts(
      sheet,
      childDataStart,
      childDataEnd,
      supplierCode,
      { partNo: header.partNo, ac: header.ac, rt: header.rt },
      isMigration,
    );

  // Collect material warnings
  for (const cp of child_parts) {
    const mid = materialIdMap.get(cp.child_part_no) ?? 0;
    if (mid === 0) {
      warnings.push({
        type: "material-not-found",
        childPartNo: cp.child_part_no,
        message: `Material code "${cp.material_code}" ไม่พบสำหรับ child part "${cp.child_part_no}"`,
      });
    }
  }

  for (const am of ambiguousMatches) {
    warnings.push({
      type: "material-ambiguous",
      childPartNo: am.childPartNo,
      message:
        am.resolvedBy === "legacy"
          ? `Material code "${am.materialCode}" ตรง attribute กับ ${am.candidateCount} รายการ — เทียบกับระบบเก่าแล้วเลือกตัวที่ตรง`
          : `Material code "${am.materialCode}" ตรง attribute กับ ${am.candidateCount} รายการ — เลือกตัวที่สร้างล่าสุด (material_id สูงสุด) โปรดตรวจสอบราคา`,
    });
  }

  for (const lr of legacyResolvedMatches) {
    warnings.push({
      type: "material-resolved-legacy",
      childPartNo: lr.childPartNo,
      message: `Material code "${lr.materialCode}" ไม่ตรง attribute โดยตรง (ชื่อ supplier/spec อาจถูกเปลี่ยนหรือย่อในระบบเก่า) — เทียบกับระบบเก่าแล้วพบ material ที่ตรงกัน โปรดตรวจสอบราคา`,
    });
  }

  // ── Part Process (OEM) ─────────────────────────────────────────────────
  const oemProcess =
    sections.oemProcessRow !== -1
      ? parsePartProcessSection(
          sheet,
          sections.oemProcessRow,
          sections.spareProcessRow !== -1
            ? sections.spareProcessRow - 1
            : sheet.rowCount,
        )
      : { processes: [], admin: 0, profit: 0 };

  // ── Part Process (Spare) ───────────────────────────────────────────────
  const spareProcess =
    sections.spareProcessRow !== -1
      ? parsePartProcessSection(
          sheet,
          sections.spareProcessRow,
          sections.oemOtherProcessRow !== -1
            ? sections.oemOtherProcessRow - 1
            : sheet.rowCount,
        )
      : { processes: [], admin: 0, profit: 0 };

  // ── Part Other Process (OEM) ───────────────────────────────────────────
  const oemOtherProcess =
    sections.oemOtherProcessRow !== -1
      ? parsePartOtherProcessSection(
          sheet,
          sections.oemOtherProcessRow,
          sections.spareOtherProcessRow !== -1
            ? sections.spareOtherProcessRow - 1
            : sheet.rowCount,
        )
      : { processes: [], admin: 0, profit: 0, price: 0 };

  // ── Part Other Process (Spare) ─────────────────────────────────────────
  const spareOtherProcess =
    sections.spareOtherProcessRow !== -1
      ? parsePartOtherProcessSection(
          sheet,
          sections.spareOtherProcessRow,
          sheet.rowCount,
        )
      : { processes: [], admin: 0, profit: 0, price: 0 };

  // ── Build result ───────────────────────────────────────────────────────
  const preview: ImportedBreakdown = {
    header: {
      part_no: header.partNo,
      part_name: header.partName,
      ac: header.ac,
      rt: header.rt,
    },
    child_parts,
    part_process: {
      oem_part_process: oemProcess.processes,
      admin_oem: oemProcess.admin,
      profit_oem: oemProcess.profit,
      spare_part_process: spareProcess.processes,
      admin_spare: spareProcess.admin,
      profit_spare: spareProcess.profit,
    },
    part_other_process: {
      oem_other_process: oemOtherProcess.processes,
      admin_oem: oemOtherProcess.admin,
      profit_oem: oemOtherProcess.profit,
      oem_price: oemOtherProcess.price,
      spare_other_process: spareOtherProcess.processes,
      admin_spare: spareOtherProcess.admin,
      profit_spare: spareOtherProcess.profit,
      spare_price: spareOtherProcess.price,
    },
  };

  // ── Fetch master data for ID resolution ────────────────────────────────
  const [
    secondTierProcesses,
    otherProcesses,
    cpProcesses,
    oemPartProcesses,
    sparePartProcesses,
    oemOtherProcesses,
    spareOtherProcesses,
  ] = await Promise.all([
    fetchMaterial2ndTierProcesses(),
    fetchMaterialOtherProcesses(),
    fetchChildPartProcesses(),
    fetchOEMPartProcesses(),
    fetchSparePartProcesses(),
    fetchOEMPartOtherProcesses(),
    fetchSparePartOtherProcesses(),
  ]);

  const nameMaps = {
    sndTierMap: buildNameMap(secondTierProcesses),
    otherMap: buildNameMap(otherProcesses),
    cpMap: buildNameMap(cpProcesses),
    oemPartMap: buildNameMap(oemPartProcesses),
    sparePartMap: buildNameMap(sparePartProcesses),
    oemOtherMap: buildNameMap(oemOtherProcesses),
    spareOtherMap: buildNameMap(spareOtherProcesses),
  };

  // Collect process warnings
  const collectProcessWarnings = (
    names: string[],
    map: Map<string, number>,
    label: string,
    type: ImportWarning["type"],
  ) => {
    const seen = new Set<string>();
    for (const name of names) {
      const key = name.toLowerCase();
      if (!map.get(key) && !seen.has(key)) {
        seen.add(key);
        warnings.push({
          type,
          processName: name,
          message: `${label} "${name}" ไม่พบใน Master Data`,
        });
      }
    }
  };

  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.second_tier_processes.map((p) => p.name)),
    nameMaps.sndTierMap,
    "2nd Tier Process",
    "material-cost-process-not-found",
  );
  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.other_processes.map((p) => p.name)),
    nameMaps.otherMap,
    "Other Process",
    "material-cost-process-not-found",
  );
  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.cp_processes.map((p) => p.name)),
    nameMaps.cpMap,
    "Child Part Process",
    "child-part-process-not-found",
  );
  collectProcessWarnings(
    oemProcess.processes.map((p) => p.name),
    nameMaps.oemPartMap,
    "OEM Part Process",
    "part-process-not-found",
  );
  collectProcessWarnings(
    spareProcess.processes.map((p) => p.name),
    nameMaps.sparePartMap,
    "Spare Part Process",
    "part-process-not-found",
  );
  collectProcessWarnings(
    oemOtherProcess.processes.map((p) => p.name),
    nameMaps.oemOtherMap,
    "OEM Other Process",
    "part-other-process-not-found",
  );
  collectProcessWarnings(
    spareOtherProcess.processes.map((p) => p.name),
    nameMaps.spareOtherMap,
    "Spare Other Process",
    "part-other-process-not-found",
  );

  const body = buildImportBody(
    preview,
    resolvedPartId,
    allMaterialCodes,
    materialIdMap,
    nameMaps,
    isMigration,
    isActive,
  );

  return { preview, body, warnings, supplierCode };
};

// ─── Submit function (after user confirms) ───────────────────────────────────

export const submitImportPart = async (
  body: import("@/api/importPartService").ImportPartBody,
  supplierCode: string,
): Promise<void> => {
  await importPart(body, supplierCode);
};

// ─── Legacy combined function ────────────────────────────────────────────────

export const importPartFromExcel = async (
  file: File,
  supplierCode: string,
): Promise<void> => {
  const { body } = await parsePartFromExcel(file, supplierCode);
  await submitImportPart(body, supplierCode);
};
